import React, {useState, useMemo, useEffect} from 'react';

import {BackendJob, createEditedJob} from "components/EditJob";
import {JobTitle} from "components/JobTitle";
import {ApplicationsList} from "components/ApplicationsList";
import {Location} from "components/BillingLocationSelector";
import {JobBoardDetailTab, Application, ApplicationStatus} from "./types";
import JobInfoModal from "./JobInfo/JobInfo";
import {PublicUrlDisplay} from "components/JobBoard/PublicUrlDisplay";

import {Container, StyledSectionTabs} from './JobBoardDetails.styles';
import Api from 'api';
import { Button, Paper, Typography } from '@mui/material';

interface JobBoardDetailsProps {
    job: BackendJob;
    applications: Application[];
    locations: Location[];
    onApplicationClick: (application: Application) => void;
}

const JobBoardDetails = ({job, applications, onApplicationClick}: JobBoardDetailsProps) => {
    const [activeTab, setActiveTab] = useState<JobBoardDetailTab>(JobBoardDetailTab.newOrViewed);
    const [isJobInfoOpen, setIsJobInfoOpen] = useState(false);

    const handleJobInfoModal = () => {
        setIsJobInfoOpen(!isJobInfoOpen);
    };

    const editedJob = createEditedJob(job);
    const [featureStatus, setFeatureStatus] = useState<{ isFeatured: boolean; status: string } | null>(null);
    const [availability, setAvailability] = useState<{ status: 'available' | 'queued' | 'unavailable'; activeCount: number; jobType: number } | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                if (!job || typeof job.id !== 'number') return;
                const [status, avail] = await Promise.all([
                    Api.getFeatureStatus(Number(job.id)).catch(() => ({ isFeatured: false, status: 'none' })),
                    Api.getFeatureAvailability(Number(job.id)).catch(() => ({ status: 'unavailable', activeCount: 0, jobType: Number(job.jobType || 0) }))
                ]);
                setFeatureStatus(status);
                setAvailability(avail as { status: 'available' | 'queued' | 'unavailable'; activeCount: number; jobType: number });
            } catch (_) {}
        };
        load();
    }, [job?.id, job?.jobType]);

    const handleFeatureEnable = async () => {
        try {
            setLoading(true);
            if (!job || typeof job.id !== 'number') return;
            const res = await Api.featureListing(Number(job.id));
            const newStatus = res.status === 'in_rotation' ? { isFeatured: true, status: 'in_rotation' } : { isFeatured: true, status: 'queued' };
            setFeatureStatus(newStatus);
            // refresh availability
            const avail = await Api.getFeatureAvailability(Number(job.id));
            setAvailability(avail as { status: 'available' | 'queued' | 'unavailable'; activeCount: number; jobType: number });
        } catch (_) {
            // noop toast pattern elsewhere; keep simple
        } finally {
            setLoading(false);
        }
    };

    const jobLocation = editedJob.location;

    const {newCandidates, shortlistedCandidates, declinedCandidates, viewedCandidates} = useMemo(
        () =>
            applications.reduce<{
                newCandidates: Application[];
                shortlistedCandidates: Application[];
                declinedCandidates: Application[];
                viewedCandidates: Application[];
            }>((acc, application) => {
                if (application.status === ApplicationStatus.posted) {
                    acc.newCandidates.push(application);
                } else if (application.status === ApplicationStatus.shortlisted) {
                    acc.shortlistedCandidates.push(application);
                } else if (application.status === ApplicationStatus.declined) {
                    acc.declinedCandidates.push(application);
                } else {
                    acc.viewedCandidates.push(application)
                }

                return acc;
            },
            {newCandidates: [], shortlistedCandidates: [], declinedCandidates: [], viewedCandidates: []}
        ),
        [applications]
    )

    const tabs = [
        {label: `New/Viewed(${newCandidates.length + viewedCandidates.length})`, onClick: () => setActiveTab(JobBoardDetailTab.newOrViewed), isActive: activeTab === JobBoardDetailTab.newOrViewed},
        {label: `Shortlisted(${shortlistedCandidates.length})`, onClick: () => setActiveTab(JobBoardDetailTab.shortlisted), isActive: activeTab === JobBoardDetailTab.shortlisted},
        {label: `Hide(${declinedCandidates.length})`, onClick: () => setActiveTab(JobBoardDetailTab.hideCandidates), isActive: activeTab === JobBoardDetailTab.hideCandidates}
    ];

    return (
        <Container>
            <JobTitle
                title={editedJob.title || ''}
                {...(jobLocation ? { location: jobLocation } : {})}
                job={job}
                disableCardStyle={true}
            />
            <PublicUrlDisplay url={job.publicUrl} />
            <StyledSectionTabs tabs={tabs} />

            {/* Featured Listing status/CTA (analogous to JobPostedPage Boost card) */}
            {featureStatus && availability && availability.status !== 'unavailable' && (
                <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid var(--primary-blue)', bgcolor: '#f5f8ff', mb: 2 }}>
                    {(() => {
                        const status = featureStatus.status; // none | in_rotation | queued | charged | filled | expired | deleted
                        if (!featureStatus.isFeatured || status === 'none') {
                            return (
                                <>
                                    <Typography fontWeight={700} sx={{ mb: 0.5 }}>⭐ Feature This Listing</Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Increase visibility on the Job Board. $25 + HST. You will only be charged when you view your first candidate while in rotation.
                                        <br/>
                                        {availability.status === 'available' ? (
                                            <span>✓ Featured slot available</span>
                                        ) : (
                                            <span>📋 Feature waitlist active ({availability.activeCount}/10 in rotation)</span>
                                        )}
                                    </Typography>
                                    <Button variant="contained" onClick={handleFeatureEnable} disabled={loading}>Feature this listing</Button>
                                </>
                            );
                        }
                        if (status === 'queued') {
                            return (
                                <>
                                    <Typography fontWeight={700} sx={{ mb: 0.5 }}>⭐ Feature Waitlist</Typography>
                                    <Typography variant="body2">Your listing is on the waitlist — we’ll notify you as soon as it becomes actively featured.</Typography>
                                </>
                            );
                        }
                        if (status === 'in_rotation') {
                            return (
                                <>
                                    <Typography fontWeight={700} sx={{ mb: 0.5 }}>⭐ Featured</Typography>
                                    <Typography variant="body2">This listing is currently Featured. You haven’t been charged yet; payment only occurs when you view your first candidate.</Typography>
                                </>
                            );
                        }
                        if (status === 'charged' || status === 'filled') {
                            return (
                                <>
                                    <Typography fontWeight={700} sx={{ mb: 0.5 }}>⭐ Featured</Typography>
                                    <Typography variant="body2">This listing was Featured and has had candidate views that triggered a charge. Receipt is sent after lifecycle events.</Typography>
                                </>
                            );
                        }
                        if (status === 'expired' || status === 'deleted') {
                            return (
                                <>
                                    <Typography fontWeight={700} sx={{ mb: 0.5 }}>⭐ Feature Ended</Typography>
                                    <Typography variant="body2">This Featured Listing has ended (expired/removed). You can feature it again if you repost.</Typography>
                                </>
                            );
                        }
                        return null;
                    })()}
                </Paper>
            )}
            {activeTab === JobBoardDetailTab.newOrViewed ? (
                <ApplicationsList 
                    onApplicationClick={onApplicationClick} 
                    applicationType={'newOrViewed'} 
                    sortedApplications={{newCandidates, shortlistedCandidates, declinedCandidates, viewedCandidates}}
                    disableCardStyle={true}
                    jobType={job.jobType}
                />
            ) : null}
            {activeTab === JobBoardDetailTab.shortlisted ? (
                <ApplicationsList 
                    onApplicationClick={onApplicationClick} 
                    applicationType={'shortlisted'} 
                    sortedApplications={{newCandidates, shortlistedCandidates, declinedCandidates, viewedCandidates}}
                    disableCardStyle={true}
                    jobType={job.jobType}
                />
            ) : null}
            {activeTab === JobBoardDetailTab.hideCandidates ? (
                <ApplicationsList 
                    onApplicationClick={onApplicationClick} 
                    applicationType={'hideCandidates'} 
                    sortedApplications={{newCandidates, shortlistedCandidates, declinedCandidates, viewedCandidates}}
                    disableCardStyle={true}
                    jobType={job.jobType}
                />
            ) : null}
        
        </Container>
    );
};

export default JobBoardDetails;
